import React, { useRef, useState } from 'react';
import { styled } from '@withjoy/joykit';
import { Close, Hamburger } from '@withjoy/joykit/icons';
import { useParentWidth } from './hooks/useParentWidth';
import { IMenuItem, WorkspaceMenu } from './WorkspaceMenu';
import { JoyLogo } from './icons/JoyLogo';
import { WorkspaceGrid } from './WorkspaceGrid';
import { ThemeToggle } from './ThemeToggle';
import { EventConfiguration } from '@apps/workspace/Workspace';
import { useEventSubTypeConfig } from './hooks/useEventSubTypeConfig';
import { EventType } from '@graphql/generated';

const MenuIcon = styled.div<{ backgroundColor: string; shadow?: string }>`
  background-color: ${props => props.backgroundColor};
  padding: 8px;
  padding-left: 12px;
  /* box-shadow: ${props => props.shadow};*/
  transition: all 350ms ease;
  transition-delay: 0ms;
  cursor: pointer;
`;

const MenuContainer = styled.div<{ width: number; offset: number; backgroundColor: string; float?: boolean; shadow?: string; open?: boolean; fullWidth?: boolean }>`
  left: ${props => props.offset}px;
  ${props => {
    if (props.float && props.open) {
      return `
      left: ${props.offset}px;
      `;
    }
    return `
    left: ${props.offset}px;
    `;
  }}
  bottom: 0;
  padding: ${props => (props.width ? '16px' : 0)};
  padding-top: 0;
  position: absolute;
  height: '100%';
  z-index: 500;
  background-color: ${props => (props.float ? `rgba(${props.backgroundColor},0.25)` : `rgba(${props.backgroundColor},1)`)};
  backdrop-filter: blur(64px);
  /*
  box-shadow: ${props => (props.float ? props.shadow : 'none')};
  */
  top: ${props => (props.float ? '-2px' : 0)};
  ${props => {
    if (props.fullWidth && props.open) {
      return `
      right: 1px;
      /*
      border-top-left-radius: 32px;
      border-top-right-radius: 32px;
     
      top: 8px;
       */
      `;
    }
    return `
    width: ${props.width}px;
    border-top-left-radius: 0;
      border-top-right-radius: 0;
      
    `;
  }};
  /*opacity: ${props => (props.open ? 1 : 0)};*/
  transition: all 350ms ease;
  transition-delay: 0ms;
  overflow: auto;
`;

const ContentContainer = styled.div<{ offset: number }>`
  left: ${props => props.offset}px;
  top: 0;
  bottom: 0;
  width: ${props => (props.offset ? `calc(100% - ${props.offset}px)` : '100%')};
  overflow: auto;
  z-index: 100;
  position: absolute;
  height: 100%;
  transition: left 350ms, width 350ms;
  transition-delay: 0ms;
  padding: 0;
`;

interface WorkspaceProps {
  eventHandle?: string;
  menuItems?: IMenuItem[];
  eventConfiguration?: EventConfiguration;
}

export interface INavTheme {
  background: string;
  backgroundFragment: string;
  font: string;
  accent: string;
  shadow: string;
}

export const LIGHT_MODE: INavTheme = {
  background: 'rgb(250, 250, 250)', // Semi-transparent white
  backgroundFragment: '250, 250, 250',
  font: 'rgba(0, 0, 0, 0.85)',
  accent: 'rgba(82, 146, 241, 1)', // Example primary accent color
  shadow: '1px 1px 1px 0px rgba(0,0,0,0.1)',
};

export const DARK_MODE: INavTheme = {
  background: 'rgb(61, 61, 66)', // Semi-transparent black
  backgroundFragment: '61, 61, 61',
  font: 'rgba(255, 255, 255, 0.85)',
  accent: 'rgb(139, 179, 240)', // Example primary accent color
  shadow: '1px 1px 1px 0px rgba(0,0,0,0.1)',
};

const HEADER_HEIGHT = '50px';
const MENU_WIDTH = 400;
const COLLAPSE_THRESHOLD = 1024;

const SHADOW = '1px 1px 1px 0px rgba(0,0,0,0.1)';

export const Workspace: React.FC<WorkspaceProps> = ({ eventHandle, eventConfiguration }) => {
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [routeComponent] = useState<React.ReactNode>(null);
  const [theme, setTheme] = useState<INavTheme>(LIGHT_MODE);
  const { eventType, eventHandle: handle } = eventConfiguration || {};
  const { navigation, tiles } = useEventSubTypeConfig(eventType ?? EventType.general, handle ?? '');
  const dashboardMenuItems = navigation;

  const ref = useRef<HTMLDivElement>(null);
  const { width } = useParentWidth(ref.current);
  let float = false;
  if (width < COLLAPSE_THRESHOLD) {
    float = true;
  }

  const handleNavToggle = () => {
    setNavigationOpen(!navigationOpen);
  };

  const handleMenuKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavToggle();
    }
  };
  const onMenuItemClick = (item: IMenuItem) => {
    if (float) {
      setNavigationOpen(false);
    }
  };
  const offset = navigationOpen && !float ? MENU_WIDTH : 0;
  const shadow = navigationOpen ? SHADOW : SHADOW; //'none';
  return (
    <div
      ref={ref}
      style={{ background: theme.background, top: 0, bottom: 0, left: 0, right: 0, zIndex: 1000, position: 'absolute', margin: 0, padding: 0 }}
    >
      <div
        style={{
          width: '100%',
          height: HEADER_HEIGHT,
          backgroundColor: theme.background,
          margin: 0,
          padding: 0,
          display: 'flex',
          borderBottom: '1px solid rgba(0,0,0,0.15)',
          boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.1)',
        }}
      >
        <MenuIcon
          tabIndex={0}
          role="button"
          aria-roledescription="Close menu"
          backgroundColor={theme.background}
          shadow={shadow}
          onClick={handleNavToggle}
          onKeyPress={handleMenuKeyPress}
          title={navigationOpen ? 'Close menu' : 'Open menu'}
        >
          {navigationOpen ? (
            <Close
              size={'md'}
              margin={'4px'}
              color={theme.accent}
            />
          ) : (
            <Hamburger
              size={'lg'}
              margin={0}
              color={theme.accent}
            />
          )}
        </MenuIcon>
        <div style={{ padding: '12px' }}>
          <JoyLogo fillColor={theme.accent} />
        </div>
        <div style={{ marginLeft: 'auto', marginRight: 0, height: '48px' }}>
          <ThemeToggle
            theme={theme}
            onToggle={() => {
              theme === LIGHT_MODE ? setTheme(DARK_MODE) : setTheme(LIGHT_MODE);
            }}
          />
        </div>
      </div>
      <div style={{ top: HEADER_HEIGHT, bottom: 0, left: 0, right: 0, position: 'absolute', margin: 0, padding: 0 }}>
        <div style={{ height: '100%', width: '100%', position: 'relative', margin: 0, padding: 0, backgroundColor: theme.background }}>
          <MenuContainer
            open={navigationOpen}
            backgroundColor={theme.backgroundFragment}
            width={MENU_WIDTH}
            fullWidth={width < 500 ? true : false}
            offset={navigationOpen ? 0 : -MENU_WIDTH}
            float={float}
            shadow={shadow}
          >
            <WorkspaceMenu
              theme={theme}
              menuItems={dashboardMenuItems}
              onMenuItemClick={onMenuItemClick}
            />
          </MenuContainer>
          <ContentContainer offset={offset}>
            {routeComponent || (
              <WorkspaceGrid
                eventHandle={eventHandle}
                eventConfiguration={eventConfiguration}
                layout={tiles}
                theme={theme}
              />
            )}
          </ContentContainer>
        </div>
      </div>
    </div>
  );
};
